import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { SEOExpansion } from "@/components/shared/SEOExpansion";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Loader2, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  KEYWORD_LANDING_PAGES,
  stateLine,
  stateNameToSlug,
} from "@/data/keywordLandingConfig";
import { US_STATES } from "@/pages/public/OpenStorePage";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Interpolate every occurrence of {state_line} in a template.
 * Cleans up double spaces and empty trailing pieces so the national
 * variant reads naturally.
 */
function fillTemplate(str, stateName) {
  const suffix = stateLine(stateName);
  return str
    .replace(/\{state_line\}/g, suffix)
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,!?])/g, "$1")
    .trim();
}

/**
 * Body used by the lead form. We store into the existing state_inquiries
 * collection so ops already knows how to work these — with the source
 * field flagged so we can measure conversion by landing page.
 */
async function submitLead(payload) {
  return axios.post(`${API_URL}/state-inquiries`, payload);
}

function KeywordLandingContent({ config, stateName }) {
  const heroHeadline = fillTemplate(config.heroHeadline, stateName);
  const metaTitle = fillTemplate(config.metaTitle, stateName);
  const metaDescription = fillTemplate(config.metaDescription, stateName);
  const heroSubheadline = fillTemplate(config.heroSubheadline, stateName);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) {
      toast.error("Please tell us your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        ...form,
        state: stateName || "National",
        message: `[${config.label}${stateName ? " · " + stateName : ""}] ${form.message}`.trim(),
      });
      setSubmitted(true);
      toast.success("Got it — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbs = useMemo(
    () => [
      { name: "Home", href: "/" },
      { name: config.label, href: `/${config.kind}` },
      ...(stateName ? [{ name: stateName, href: `/${config.kind}/${stateNameToSlug(stateName)}` }] : []),
    ],
    [config, stateName]
  );

  const jsonLd = useMemo(() => {
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: config.faqs.map((f) => ({
        "@type": "Question",
        name: fillTemplate(f.q, stateName),
        acceptedAnswer: { "@type": "Answer", text: fillTemplate(f.a, stateName) },
      })),
    };
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `https://primemediawireless.com${b.href}`,
      })),
    };
    return JSON.stringify([faqData, breadcrumbData]);
  }, [config, stateName, breadcrumbs]);

  return (
    <PublicLayout>
      <SEOHead title={metaTitle} description={metaDescription} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />

      {/* Hero */}
      <section className="bg-navy py-16 sm:py-20" data-testid={`landing-hero-${config.kind}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-xs text-slate-400 mb-6" aria-label="Breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={b.href}>
                {i > 0 && <span className="mx-1">/</span>}
                {i === breadcrumbs.length - 1 ? (
                  <span className="text-white">{b.name}</span>
                ) : (
                  <Link to={b.href} className="hover:text-teal">{b.name}</Link>
                )}
              </span>
            ))}
          </nav>
          <p className="label-style text-teal mb-4">{config.heroEyebrow}</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
            {heroHeadline}
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl mb-8 font-body">{heroSubheadline}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#lead-form"
              className="inline-flex items-center gap-2 bg-teal text-white px-6 py-3 rounded-full font-body hover:bg-teal/90 transition-colors"
              data-testid={`landing-cta-primary-${config.kind}`}
            >
              {config.primaryCTA}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/open-a-store"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-full font-body hover:bg-white/10 transition-colors"
            >
              See our Total Wireless network
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">
            Why partner with Prime Media Wireless
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {config.benefits.map((b) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-5 rounded-2xl border border-slate-200 hover:border-teal transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-teal mb-3" />
                <h3 className="font-heading font-semibold text-navy mb-1">{b.title}</h3>
                <p className="text-sm text-slate-600 font-body">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* State callout when we have one */}
      {stateName && (
        <section className="py-14 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <MapPin className="w-6 h-6 text-teal mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-navy mb-3">
              Currently serving operators across {stateName}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto font-body mb-6">
              Prime Media Wireless already supports Total Wireless dealers in every corner of {stateName}. Tell us your target market and we&apos;ll share what&apos;s open, what&apos;s working, and what to avoid.
            </p>
            <Link
              to={`/${stateNameToSlug(stateName)}`}
              className="inline-flex items-center gap-2 text-teal font-body font-semibold hover:underline"
            >
              See Total Wireless stores in {stateName}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {config.faqs.map((f) => (
              <div key={f.q} className="border-l-4 border-teal pl-4">
                <h3 className="font-heading font-semibold text-navy mb-1">
                  {fillTemplate(f.q, stateName)}
                </h3>
                <p className="text-sm text-slate-600 font-body">
                  {fillTemplate(f.a, stateName)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="lead-form" className="py-16 bg-navy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-3 text-center">
            Let&apos;s talk
          </h2>
          <p className="text-slate-300 text-center mb-8 font-body">
            Tell us a bit about you and we&apos;ll get back to you within one business day.
          </p>
          {submitted ? (
            <div className="bg-white rounded-2xl p-8 text-center" data-testid="landing-lead-submitted">
              <CheckCircle className="w-10 h-10 text-teal mx-auto mb-3" />
              <p className="font-heading font-semibold text-navy">Thanks — we&apos;ll be in touch shortly.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-6 sm:p-8 space-y-4"
              data-testid={`landing-lead-form-${config.kind}`}
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required placeholder="First name*" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="landing-first-name" />
                <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="landing-last-name" />
                <Input required type="email" placeholder="Email*" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="landing-email" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="landing-phone" />
              </div>
              <Textarea
                placeholder={`Tell us about your goals${stateName ? " in " + stateName : ""}...`}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                data-testid="landing-message"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-teal hover:bg-teal/90 text-white"
                data-testid={`landing-submit-${config.kind}`}
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send my info
              </Button>
              <p className="text-xs text-slate-500 text-center">
                We&apos;ll never share your info. Unsubscribe any time.
              </p>
            </form>
          )}
        </div>
      </section>

      <SEOExpansion />
    </PublicLayout>
  );
}

export default function KeywordLandingPage({ kind }) {
  const params = useParams();
  const config = KEYWORD_LANDING_PAGES[kind];
  const stateMatch = params.stateSlug
    ? Object.values(US_STATES).find((s) => stateNameToSlug(s.name) === params.stateSlug)
    : null;
  const stateName = stateMatch ? stateMatch.name : "";
  const invalidState = !!(params.stateSlug && !stateMatch);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind, stateName]);

  if (!config) return <Navigate to="/" replace />;
  if (invalidState) return <Navigate to={`/${kind}`} replace />;

  return <KeywordLandingContent config={config} stateName={stateName} />;
}
